import { Badge, Button, Group, Progress, Stack, Table, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { ACTIVE, formatDate, formatDuration, post, type Job, type JobStatus } from "../lib/api";
import { useRegistry } from "../lib/hooks";
import { notifyError } from "./Errors";

const COLORS: Record<JobStatus, string> = {
  queued: "gray",
  running: "blue",
  succeeded: "teal",
  failed: "red",
  cancelled: "yellow",
};
const LABELS: Record<JobStatus, string> = {
  queued: "Queued",
  running: "Running",
  succeeded: "Finished",
  failed: "Failed",
  cancelled: "Cancelled",
};

export function JobStatusBadge({ status }: { status: JobStatus }) {
  return <Badge color={COLORS[status]} variant="light">{LABELS[status]}</Badge>;
}

export function useKindLabel() {
  const { data } = useRegistry();
  return (kind: string) => data?.job_kinds.find((k) => k.key === kind)?.label ?? kind;
}

export function CancelButton({ job, admin = false, label = "Cancel" }: { job: Job; admin?: boolean; label?: string }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => post<Job>(admin ? `/admin/jobs/${job.id}/cancel` : `/jobs/${job.id}/cancel`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job", job.id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["admin-jobs"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => notifyError(e),
  });
  if (!ACTIVE.includes(job.status)) return null;
  return (
    <Button size="xs" color="red" variant="light" loading={m.isPending} onClick={() => m.mutate()}>
      {label}
    </Button>
  );
}

export function JobProgress({ job }: { job: Job }) {
  if (job.status === "queued") {
    if (job.queue === "qe") {
      const n = job.queue_position;
      return <Text size="sm" c="dimmed" data-testid="queue-position">Waiting in the Quantum ESPRESSO queue
        {n ? (n === 1 ? ": next to run" : `: position ${n} (${n - 1} job${n === 2 ? "" : "s"} ahead)`) : "…"}</Text>;
    }
    return <Text size="sm" c="dimmed">Waiting for a free worker ({job.queue} queue)…</Text>;
  }
  if (job.status !== "running") return null;
  return (
    <Stack gap={4}>
      <Progress value={Math.round(job.progress * 100)} animated aria-label="Progress" />
      <Group justify="space-between">
        <Text size="xs" c="dimmed">{job.message}</Text>
        <Text size="xs" c="dimmed">{Math.round(job.progress * 100)}%{job.metric != null ? ` · ${job.metric.toFixed(1)} meV` : ""}</Text>
      </Group>
    </Stack>
  );
}

export function JobsTable({ jobs, showProject = true }: { jobs: Job[]; showProject?: boolean }) {
  const label = useKindLabel();
  if (!jobs.length) return <Text c="dimmed" size="sm">No calculations yet.</Text>;
  return (
    <Table.ScrollContainer minWidth={560}>
      <Table highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Calculation</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Started</Table.Th>
            <Table.Th>CPU time</Table.Th>
            {showProject && <Table.Th>Project</Table.Th>}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {jobs.map((j) => (
            <Table.Tr key={j.id}>
              <Table.Td><Link to={`/jobs/${j.id}`}>{label(j.kind)}</Link></Table.Td>
              <Table.Td><JobStatusBadge status={j.status} /></Table.Td>
              <Table.Td>{formatDate(j.started_at ?? j.created_at, true)}</Table.Td>
              <Table.Td>{j.cpu_seconds ? formatDuration(j.cpu_seconds) : "-"}</Table.Td>
              {showProject && <Table.Td>{j.project_id ? <Link to={`/projects/${j.project_id}`}>open</Link> : "-"}</Table.Td>}
              <Table.Td><CancelButton job={j} /></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
